// Builds an asana page's SEO tags from the data the admin already uploaded
// (names, benefits, how-to text, main image), so nothing needs typing per asana.

export const SITE_URL = "https://yogarefer.com";

// Rich-text fields can be HTML strings, JSON-wrapped HTML, or plain arrays
const toText = (field) => {
  if (!field) return "";
  const parts = Array.isArray(field) ? field : [field];
  return parts
    .map((part) => {
      let html = typeof part === "string" ? part : "";
      try {
        const parsed = JSON.parse(html);
        if (typeof parsed === "string") html = parsed;
      } catch {
        /* plain text or HTML: use as is */
      }
      html = html.replace(/<\/(p|li|h[1-6]|div|tr|td|th)>/gi, " ");
      return new DOMParser().parseFromString(html, "text/html").body.textContent || "";
    })
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();
};

const excerpt = (text, max) =>
  text.length <= max ? text : text.slice(0, max).replace(/\s+\S*$/, "") + "…";

export const buildAsanaSeo = (asana, slug) => {
  const name = (asana.name || "").trim();
  const english = (asana.english || "").trim();
  const label =
    english && english.toLowerCase() !== name.toLowerCase()
      ? `${name} (${english})`
      : name;

  const body = toText(asana.benefits) || toText(asana.howToDo);
  const poseType = Array.isArray(asana.poseType) ? asana.poseType.join(", ") : "";
  const fallback = `Learn how to do ${label}${
    poseType ? `, a ${poseType.toLowerCase()} yoga pose` : ""
  }: steps, benefits, precautions and variations.`;

  return {
    title: `${label} – Benefits, How to Do & Variations | YogaRefer`,
    description: body ? excerpt(`${label}: ${body}`, 155) : fallback,
    canonical: `${SITE_URL}/asanas/${slug}`,
    image: asana.mainImage || "",
  };
};

// Sets the tags and returns a function that puts the previous values back,
// so leaving the asana page doesn't leave its tags on the next page.
export const applySeo = ({ title, description, canonical, image }) => {
  const undo = [];

  const setTag = (selector, create, attr, value) => {
    if (!value) return;
    let el = document.head.querySelector(selector);
    const created = !el;
    if (!el) {
      el = create();
      document.head.appendChild(el);
    }
    const previous = created ? null : el.getAttribute(attr);
    el.setAttribute(attr, value);
    undo.push(() => {
      if (created) el.remove();
      else if (previous === null) el.removeAttribute(attr);
      else el.setAttribute(attr, previous);
    });
  };

  const meta = (key, value, by = "name") =>
    setTag(
      `meta[${by}="${key}"]`,
      () => {
        const el = document.createElement("meta");
        el.setAttribute(by, key);
        return el;
      },
      "content",
      value,
    );

  const previousTitle = document.title;
  document.title = title;
  undo.push(() => {
    document.title = previousTitle;
  });

  setTag(
    'link[rel="canonical"]',
    () => {
      const el = document.createElement("link");
      el.rel = "canonical";
      return el;
    },
    "href",
    canonical,
  );

  meta("title", title);
  meta("description", description);
  meta("og:type", "article", "property");
  meta("og:url", canonical, "property");
  meta("og:title", title, "property");
  meta("og:description", description, "property");
  meta("og:image", image, "property");
  meta("twitter:title", title);
  meta("twitter:description", description);
  meta("twitter:image", image);

  return () => undo.forEach((fn) => fn());
};
